import { lazy, Suspense, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useLocation,
} from 'react-router-dom';

import { ContentRoute, LayoutSplashScreen } from '../_metronic/layout';
import { CSATSurveyModal } from './components/CSATSurveyModal';
import { OabCaptureTriggerProvider } from './modules/OabCapture/OabCaptureTriggerContext';
import OabCaptureValueMomentModal from './modules/OabCapture/OabCaptureValueMomentModal';
import ModalMonitoringSales from './components/ModalMonitoringSales';
import TimeBasedDisplay from './components/ModalMonitoringSales/TimeBasedDisplay';
import { MODAL_MONITORING_INTERVAL_MS } from './constants/CronTimes';
import { ROUTES_PATH } from './constants/Routes';
import { OnboardingWelcomeGate } from './pages/OnboardingWelcome/OnboardingWelcomeGate';
import { OnboardingAddressGate } from './pages/OnboardingAddress/OnboardingAddressGate';
import { OnboardingOabGate } from './pages/OnboardingOab/OnboardingOabGate';
import { OnboardingOfficeGate } from './pages/OnboardingOffice/OnboardingOfficeGate';
import { OnboardingToolsGate } from './pages/OnboardingTools/OnboardingToolsGate';
import { toast } from 'react-toastify';
import WithFeatureFlag from './components/hoc/WithFeatureFlag';
import { FEATURE_FLAGS } from './constants/FeatureFlag';
import {
  JusfyPayCharges,
  JusfyPayWallet,
  JusfyPayProfile,
  JusfyPayNewCharge,
  JusfyPaySimulator,
} from './pages/Pay/frame/subpages';

import useCredits from './hooks/useCredits';
import { useOnboardingCreditsPolling } from './hooks/useOnboardingCreditsPolling';
import {
  getRestrictedPaywallRedirectPath,
  isRestrictedPaywallExemptPath,
  shouldRedirectToPaywall,
} from './helpers/restrictedPaywallRoutes';
import {
  getAcademyRedirectPath,
  isAcademyAllowedPath,
} from './helpers/academyAllowedRoutes';
import useIsAcademyUser from './hooks/useIsAcademyUser';

import { MeuDrive } from './pages/MeuDrive/frame/subpages';
// ---------------------------------------------------------------------------
// Lazy-loaded pages (code splitting) — cada página vira um chunk separado
// Para named exports: .then(m => ({ default: m.NomeDoExport }))
// Para default exports: basta o import()
// ---------------------------------------------------------------------------

// Páginas — named exports
const Agenda = lazy(() =>
  import('./pages/Agenda').then((m) => ({ default: m.Agenda })),
);
const Aluguel = lazy(() =>
  import('./pages/Aluguel').then((m) => ({ default: m.Aluguel })),
);
const Assinaturas = lazy(() =>
  import('./pages/Assinaturas').then((m) => ({ default: m.Assinaturas })),
);
const AssinaturasForm = lazy(() =>
  import('./pages/AssinaturasForm').then((m) => ({
    default: m.AssinaturasForm,
  })),
);
const Atualizacao = lazy(() =>
  import('./pages/Atualizacao').then((m) => ({ default: m.Atualizacao })),
);
const EditReport = lazy(() =>
  import('./pages/EditReport').then((m) => ({ default: m.EditReport })),
);
const AtualizacaoCompleto = lazy(() =>
  import('./pages/AtualizacaoCompleto').then((m) => ({
    default: m.AtualizacaoCompleto,
  })),
);
const Calculators = lazy(() =>
  import('./pages/Calculadoras').then((m) => ({ default: m.Calculators })),
);
const Clientes = lazy(() =>
  import('./pages/Clientes').then((m) => ({ default: m.Clientes })),
);
const ClientesDetalhe = lazy(() =>
  import('./pages/Clientes').then((m) => ({ default: m.ClientesDetalhe })),
);
const ClientesArquivados = lazy(() =>
  import('./pages/Clientes').then((m) => ({ default: m.ClientesArquivados })),
);
const ClientesOabSugestoes = lazy(() =>
  import('./pages/Clientes').then((m) => ({
    default: m.ClientesOabSugestoes,
  })),
);
const Consulta = lazy(() =>
  import('./pages/Consulta').then((m) => ({ default: m.Consulta })),
);
const Dashboard = lazy(() =>
  import('./pages/Dashboard').then((m) => ({ default: m.Dashboard })),
);
const Fgts = lazy(() =>
  import('./pages/Fgts').then((m) => ({ default: m.Fgts })),
);
const Jurisprudencia = lazy(() =>
  import('./pages/Jurisprudencia').then((m) => ({ default: m.Jurisprudencia })),
);

const JurisprudenciaCompleta = lazy(() =>
  import('./pages/JurisprudenciaCompleta').then((m) => ({
    default: m.JurisprudenciaCompleta,
  })),
);
const JusGPT = lazy(() =>
  import('./pages/JusGPT').then((m) => ({ default: m.JusGPT })),
);
const PageListInfluencer = lazy(() =>
  import('./pages/ListaCadastrosInfluencers').then((m) => ({
    default: m.PageListInfluencer,
  })),
);
const Oportunidades = lazy(() =>
  import('./pages/Oportunidades').then((m) => ({ default: m.Oportunidades })),
);
const ContratarCorrespondente = lazy(() =>
  import('./pages/Diligencias/ContratarCorrespondente').then((m) => ({
    default: m.ContratarCorrespondente,
  })),
);
const Pensao = lazy(() =>
  import('./pages/Pensao').then((m) => ({ default: m.Pensao })),
);
const PerfilRoute = lazy(() =>
  import('./routes/PerfilRoute').then((m) => ({ default: m.PerfilRoute })),
);
const Escritorio = lazy(() =>
  import('./pages/Escritorio').then((m) => ({ default: m.Escritorio })),
);
const PeticaoVisualizar = lazy(() =>
  import('./pages/PeticaoVisualizar').then((m) => ({
    default: m.PeticaoVisualizar,
  })),
);
const PeticoesListagem = lazy(() =>
  import('./pages/PeticoesListagem').then((m) => ({
    default: m.PeticoesListagem,
  })),
);
const PeticaoSelecionadaVisualizar = lazy(() =>
  import('./pages/PeticoesSelecionadas').then((m) => ({
    default: m.PeticaoSelecionadaVisualizar,
  })),
);
const PeticoesSelecionadas = lazy(() =>
  import('./pages/PeticoesSelecionadas').then((m) => ({
    default: m.PeticoesSelecionadas,
  })),
);
const Prev = lazy(() =>
  import('./pages/Prev').then((m) => ({ default: m.Prev })),
);
const Revisional = lazy(() =>
  import('./pages/Revisional').then((m) => ({ default: m.Revisional })),
);
const About = lazy(() =>
  import('./pages/Sobre').then((m) => ({ default: m.About })),
);
const Telas = lazy(() =>
  import('./pages/Telas').then((m) => ({ default: m.Telas })),
);
const Trabalhista = lazy(() =>
  import('./pages/Trabalhista').then((m) => ({ default: m.Trabalhista })),
);
const Divorcio = lazy(() =>
  import('./pages/Divorcio/App').then((m) => ({ default: m.Divorcio })),
);
const JusPage = lazy(() =>
  import('./pages/JusPage').then((m) => ({ default: m.JusPage })),
);
const JusSpace = lazy(() =>
  import('./pages/JusSpace').then((m) => ({ default: m.JusSpace })),
);
const DomainFormPage = lazy(() =>
  import('./pages/JusSpace/components/DomainFormPage').then((m) => ({
    default: m.DomainFormPage,
  })),
);
const WhatsAppBusiness = lazy(() =>
  import('./pages/JusZap').then((m) => ({ default: m.WhatsAppBusiness })),
);
const Gamification = lazy(() =>
  import('./pages/Gamification').then((m) => ({ default: m.Gamification })),
);
const AfiliadosRoute = lazy(() =>
  import('./routes/AfiliadosRoute').then((m) => ({
    default: m.AfiliadosRoute,
  })),
);
const JusfinderWrapper = lazy(() =>
  import('./pages/flat/jusfinderwrapper').then((m) => ({
    default: m.JusfinderWrapper,
  })),
);
const VehicleTracking = lazy(() =>
  import('./pages/flat/JusfinderUnivesal/Pages/VehicleTracking').then((m) => ({
    default: m.VehicleTracking,
  })),
);
const MAIN = lazy(() =>
  import('./pages/flat/JusfinderUnivesal/Pages/Main').then((m) => ({
    default: m.MAIN,
  })),
);
const AcimaTetoCalculator = lazy(() =>
  import('./pages/Acima-Teto-Calculator').then((m) => ({
    default: m.AcimaTetoCalculator,
  })),
);
const PensionCalculator = lazy(() =>
  import('./pages/Pension-Calculator').then((m) => ({
    default: m.PensionCalculator,
  })),
);
// feat-planejamento-calculadora-standalone: calculadora de planejamento própria
// (mesma máquina de entrada da previdenciária; passo 3 = planejamento).
const PensionPlanningCalculator = lazy(() =>
  import('./pages/Pension-Calculator').then((m) => ({
    default: m.PensionPlanningCalculator,
  })),
);

// Páginas — default exports
const Calculos = lazy(() => import('./pages/Calculos'));
const CasesManager = lazy(() => import('./pages/CasesManager'));
const Dosimetry = lazy(() => import('./pages/Dosimetria'));
const OverDebtsCalculator = lazy(() => import('./pages/Endividamento'));
const InssNews = lazy(() => import('./pages/INSSNEWS'));
const ReportsPage = lazy(() => import('./pages/JusGPT/Reports'));
const JusGPTBusinessProvider = lazy(() =>
  import('./pages/JusGPT/context/BusinessClientProvider'),
);
const Config = lazy(() => import('./pages/JusGPT/pages/Config'));
const PASEP = lazy(() => import('./pages/PASEP'));
const Penal = lazy(() => import('./pages/ProgessaoRegime'));
const Rmc = lazy(() => import('./pages/RMC'));
const LawsuitsMonitor = lazy(() => import('./pages/flat/LawsuitsMonitor'));
const SalesLandingPage = lazy(() =>
  import('./pages/flat/LawsuitsMonitor/components/Page/SalesLandingPage'),
);
const LawsuitMonitorV2 = lazy(() =>
  import('./pages/flat/LawsuitsMonitor/components/Page/LawsuitMonitorV2'),
);
const ProcessualQuery = lazy(() => import('./pages/flat/ProcessualQuery'));
const TermsOfUse = lazy(() => import('./pages/flat/TermsOfUse'));
const JusMailPage = lazy(() => import('./pages/JusMailPage'));
const JusZapLpPage = lazy(() => import('./pages/JusZapLpPage'));
const BlogEditorPage = lazy(() => import('./pages/JusPage/BlogEditorPage'));
const TeamManagement = lazy(() =>
  import('./pages/JusZap/components/TeamManagement'),
);
const RobotSettings = lazy(() =>
  import('./pages/JusZap/components/RobotSettings'),
);
const CustomFlowConfiguration = lazy(() =>
  import('./pages/JusZap/components/CustomFlowConfiguration'),
);
const Deadlines = lazy(() => import('./pages/flat/Deadlines'));
const PointsHistory = lazy(() =>
  import('./pages/Gamification/components/PointsHistory'),
);
const MeetingScheduler = lazy(() =>
  import('./pages/JusZap/components/MeetingScheduler'),
);
const JusrecrutaEmbed = lazy(() =>
  import('./pages/JusrecrutaEmbed').then((m) => ({
    default: m.JusrecrutaEmbed,
  })),
);
const BusinessLpGuard = lazy(() =>
  import('./pages/JusGPT/components/BusinessLpGuard'),
);
const ContabilidadeLpGuard = lazy(() =>
  import('./pages/JusCont/components/ContabilidadeLpGuard'),
);
const JusContContatoEspecialista = lazy(() =>
  import('./pages/JusCont/pages/ContatoEspecialista'),
);
const ContabilidadePainelGuard = lazy(() =>
  import('./pages/JusCont/components/ContabilidadePainelGuard'),
);
const JusContPainelNotasFiscais = lazy(() =>
  import('./pages/JusCont/pages/PainelNotasFiscais'),
);
const JusContNotasEmitidas = lazy(() =>
  import('./pages/JusCont/pages/PainelNotasFiscais/NotasEmitidas'),
);
const CapicandiaCheckout = lazy(() => import('./pages/CapicandiaCheckout'));
const JusClass = lazy(() =>
  import('./pages/JusClass').then((m) => ({ default: m.JusClass })),
);

const JusMailRedirect = lazy(() => import('./pages/JusMailRedirect'));
const PlanAndPayment = lazy(() =>
  import('./pages/PlanAndPayment').then((m) => ({
    default: m.PlanAndPayment,
  })),
);

const Security = lazy(() =>
  import('./pages/Security').then((m) => ({
    default: m.Security,
  })),
);

// Páginas protegidas com feature flag (lazy + HOC)
const ProtectedUsuarios = lazy(() =>
  import('./pages/Usuarios').then((m) => ({
    default: WithFeatureFlag(
      m.Usuarios,
      FEATURE_FLAGS.PERMISSION.ENABLE_SUBUSER_CREATION,
    ).RedirectTo('/dashboard'),
  })),
);
const ProtectedUsuariosForm = lazy(() =>
  import('./pages/UsuariosForm').then((m) => ({
    default: WithFeatureFlag(
      m.UsuariosForm,
      FEATURE_FLAGS.PERMISSION.ENABLE_SUBUSER_CREATION,
    ).RedirectTo('/dashboard'),
  })),
);
// Release por usuário (`InstagramCreatorLp`, com `perUser`): sem a flag vai para
// /dashboard. Com ela, sem o checkout INSTAGRAM no addon manager, mostra a LP.
const ProtectedCriadorConteudo = lazy(() =>
  import('./pages/flat/CriadorConteudo/components/InstagramAccessGuard').then(
    (m) => ({
      default: WithFeatureFlag(
        m.default,
        FEATURE_FLAGS.RELEASE.INSTAGRAM_CREATOR_LP,
        { perUser: true },
      ).RedirectTo('/dashboard'),
    }),
  ),
);
/*
  `perUser` NÃO é opcional aqui, e o motivo está no outro repo.

  A rota `/api/v2/jus-score-rh` é gateada por `requireFeatureFlag`, que resolve
  a flag com `hasUserPermission(flag, userId)` — ou seja, casa por user_id e por
  nome de grupo. Sem `perUser`, o front pergunta `is-enabled` SEM contexto: com
  uma allowlist de usuário no Unleash (o que o backend precisa para funcionar),
  a resposta é falsa e o módulo fica escondido até para quem foi liberado.

  Com ele, os dois lados avaliam a MESMA flag do MESMO jeito: release com `perUser`.
*/
const ProtectedJusScoreRh = lazy(() =>
  import('./pages/JusScoreRH').then((m) => ({
    default: WithFeatureFlag(m.JusScoreRh, FEATURE_FLAGS.RELEASE.JUSCORE_RH, {
      perUser: true,
    }).RedirectTo('/dashboard'),
  })),
);
const ProtectedJusScoreRhDetalhes = lazy(() =>
  import('./pages/JusScoreRH').then((m) => ({
    default: WithFeatureFlag(
      m.JusScoreRhDetalhes,
      FEATURE_FLAGS.RELEASE.JUSCORE_RH,
      { perUser: true },
    ).RedirectTo('/dashboard'),
  })),
);
const ProtectedJusScoreRhPastas = lazy(() =>
  import('./pages/JusScoreRH').then((m) => ({
    default: WithFeatureFlag(
      m.JusScoreRhPastas,
      FEATURE_FLAGS.RELEASE.JUSCORE_RH,
      { perUser: true },
    ).RedirectTo('/dashboard'),
  })),
);
const ProtectedJusScoreRhPasta = lazy(() =>
  import('./pages/JusScoreRH').then((m) => ({
    default: WithFeatureFlag(m.JusScoreRhPasta, FEATURE_FLAGS.RELEASE.JUSCORE_RH, {
      perUser: true,
    }).RedirectTo('/dashboard'),
  })),
);
const ProtectedAntecipacaoComoFunciona = lazy(() =>
  import('./pages/AntecipacaoProcessos/frame/subpages').then((m) => ({
    default: WithFeatureFlag(
      m.AntecipacaoComoFunciona,
      FEATURE_FLAGS.KILL_SWITCH.ANTECIPACAO_PROCESSOS,
    ).RedirectTo(ROUTES_PATH.JUSFYPAY.CHARGES),
  })),
);
const ProtectedAntecipacaoMeuPainel = lazy(() =>
  import('./pages/AntecipacaoProcessos/frame/subpages').then((m) => ({
    default: WithFeatureFlag(
      m.AntecipacaoMeuPainel,
      FEATURE_FLAGS.KILL_SWITCH.ANTECIPACAO_PROCESSOS,
    ).RedirectTo(ROUTES_PATH.JUSFYPAY.CHARGES),
  })),
);

export default function BasePage() {
  const dispatch = useDispatch();
  const authToken = useSelector((state) => state.auth.authToken);
  const is_profile_survey_open = useSelector(
    (state) => state.app.is_profile_survey_open,
  );
  const juszapConfiguration = useSelector(
    (state) => state.app.juszap_configuration,
  );
  const isJuszapConfigurationLoading = useSelector(
    (state) => state.app.is_juszap_configuration_loading,
  );
  const isJuszapConfigurationError = useSelector(
    (state) => state.app.is_juszap_configuration_error,
  );
  const history = useHistory();
  const location = useLocation();
  const {
    isRestricted,
    isLoading,
    isSubscriptionStatusLoading,
  } = useCredits();
  const { isPolling: isPollingCredits } = useOnboardingCreditsPolling();
  const { isAcademyUser, isLoading: isAcademyUserLoading } =
    useIsAcademyUser();

  /* Plano academy: o aluno tem seis ferramentas, e o menu esconde o resto.
     Esconder item de menu não protege rota — deep link, favorito e histórico
     continuam abrindo /meus-processos e companhia. Aqui ele volta para a home.
     A guarda é feita na renderização (antes do <Switch>, abaixo), e não num
     useEffect: com efeito, a página proibida chegava a montar e rodar os seus
     próprios efeitos (requests, tracking) antes do redirect. Espera o valor
     confirmado (isAcademyUserLoading) porque um redirect feito pelo palpite do
     localStorage tiraria um advogado da página dele. */
  const mustRedirectAcademyUser =
    isAcademyUser &&
    !isAcademyUserLoading &&
    !isAcademyAllowedPath(location.pathname);

  /* O outro lado da guarda: as telas do aluno (embed do JusRecruta) só existem
     para o aluno. Sem isso, /estudos, /academy e /carreira abriam o iframe para
     qualquer logado que digitasse a URL — e o SSO dele no JusRecruta viraria um
     usuário FIRM lá. Em produção sem REACT_APP_ACADEMY_PLAN_IDS ninguém é
     aluno, então isto DESLIGA as rotas sem apagar nada. Espera o valor
     confirmado pelo mesmo motivo do redirect acima. */
  const academyOnly = (render) => (routeProps) => {
    if (isAcademyUserLoading) return <LayoutSplashScreen />;
    if (!isAcademyUser) return <Redirect to={ROUTES_PATH.MAIN.DASHBOARD} />;
    return render(routeProps);
  };


  const shouldRedirectToProfileSurvey = is_profile_survey_open;
  const isExemptFromOnboardingGates = isRestrictedPaywallExemptPath(
    location.pathname,
  );

  // Carregar JusZap Configuration se não estiver carregada e não houver erro anterior
  useEffect(() => {
    if (
      authToken &&
      !juszapConfiguration &&
      !isJuszapConfigurationLoading &&
      !isJuszapConfigurationError
    ) {
      dispatch({
        type: 'LOAD_JUSZAP_CONFIGURATION',
        payload: { authToken },
      });
    }
  }, [
    authToken,
    juszapConfiguration,
    isJuszapConfigurationLoading,
    isJuszapConfigurationError,
  ]);

  useEffect(() => {
    if (shouldRedirectToProfileSurvey) {
      history.push('/profile/survey');
      dispatch({ type: 'OPEN_PROFILE_SURVEY' });
    }
  }, []);

  useEffect(() => {
    if (
      shouldRedirectToPaywall({
        isRestricted,
        isSubscriptionStatusLoading,
        isExemptPath: isRestrictedPaywallExemptPath(location.pathname),
        isPollingCredits,
      })
    ) {
      toast.error(
        'Seu plano precisa ser reativado. Você foi redirecionado para a página de plano e pagamento.',
      );
      history.push(getRestrictedPaywallRedirectPath());
    }
  }, [
    isRestricted,
    isSubscriptionStatusLoading,
    isPollingCredits,
    location.pathname,
    history,
  ]);

  if (
    isLoading ||
    (isRestricted && !isRestrictedPaywallExemptPath(location.pathname))
  ) {
    return <LayoutSplashScreen />;
  }

  if (mustRedirectAcademyUser) {
    return <Redirect to={getAcademyRedirectPath()} />;
  }

  return (
    <OabCaptureTriggerProvider>
    <Suspense fallback={<LayoutSplashScreen />}>
      <CSATSurveyModal />
      <OabCaptureValueMomentModal />
      <TimeBasedDisplay
        timeInterval={MODAL_MONITORING_INTERVAL_MS}
        isActive={false}
      >
        <ModalMonitoringSales />
      </TimeBasedDisplay>

      <OnboardingWelcomeGate isExempt={isExemptFromOnboardingGates}>
      <OnboardingAddressGate isExempt={isExemptFromOnboardingGates}>
      <OnboardingOabGate isExempt={isExemptFromOnboardingGates}>
        <OnboardingOfficeGate isExempt={isExemptFromOnboardingGates}>
          <OnboardingToolsGate isExempt={isExemptFromOnboardingGates}>
          <Switch>
            {
              /* Redirect from root URL to /dashboard. */
              <Redirect exact from="/" to="/dashboard" />
            }
            <Redirect
              exact
              from="/configuracoes/perfil"
              to={{
                pathname: ROUTES_PATH.CONFIGURACOES.ACCOUNT_PROFILE,
                search: location.search,
              }}
            />
            <Redirect
              exact
              from="/configuracoes/plano-e-pagamento"
              to={{
                pathname: ROUTES_PATH.CONFIGURACOES.PLAN_AND_PAYMENT,
                search: location.search,
              }}
            />
            <Redirect exact from="/jusfypay" to={ROUTES_PATH.JUSFYPAY.CHARGES} />
            <Redirect
              exact
              from="/jusfypay/payment-links"
              to={{
                pathname: ROUTES_PATH.JUSFYPAY.CHARGES,
                search: location.search,
              }}
            />
            <Redirect
              exact
              from="/jusfypay/payments"
              to={{
                pathname: ROUTES_PATH.JUSFYPAY.CHARGES,
                search: location.search,
              }}
            />
            <Redirect
              exact
              from={ROUTES_PATH.JUSFYPAY.ANTECIPACAO}
              to={ROUTES_PATH.JUSFYPAY.ANTECIPACAO_COMO_FUNCIONA}
            />
            <ContentRoute path="/meu-drive" component={MeuDrive} />
            <ContentRoute path="/dashboard" component={Dashboard} />
            <ContentRoute path="/relatorio/editar" component={EditReport} />
            <ContentRoute path="/jusfypay/charges" component={JusfyPayCharges} />
            <ContentRoute path="/jusfypay/wallet" component={JusfyPayWallet} />
            <ContentRoute path="/jusfypay/profile" component={JusfyPayProfile} />
            <ContentRoute
              path="/jusfypay/new-charge"
              component={JusfyPayNewCharge}
            />
            <ContentRoute
              path="/jusfypay/simulator"
              component={JusfyPaySimulator}
            />
            <ContentRoute
              path={ROUTES_PATH.JUSFYPAY.ANTECIPACAO_COMO_FUNCIONA}
              component={ProtectedAntecipacaoComoFunciona}
            />
            <ContentRoute
              path={ROUTES_PATH.JUSFYPAY.ANTECIPACAO_MEU_PAINEL}
              component={ProtectedAntecipacaoMeuPainel}
            />
            <ContentRoute path="/telasPerfilUsuario" component={Telas} />
            <ContentRoute path="/pensao/:calculo_id/:status?" component={Pensao} />
            <ContentRoute path="/pensao" component={Pensao} />
            <ContentRoute
              path="/aluguel/:calculo_id/:status?"
              component={Aluguel}
            />
            <ContentRoute path="/aluguel" component={Aluguel} />
            <ContentRoute path="/fgts/:calculo_id/:status?" component={Fgts} />
            <ContentRoute path="/fgts" component={Fgts} />
            <ContentRoute path="/calculadora_cnis/:calculo_id" component={Prev} />
            <ContentRoute path="/calculadora_cnis" component={Prev} />
            <ContentRoute path="/cases" component={CasesManager} />
            <ContentRoute path="/vehicletracking" component={VehicleTracking} />
            <ContentRoute
              path="/jusfinder/:page?/:query?/:type(\w+)?/:document([^/]+)?"
              component={JusfinderWrapper}
            />
            <ContentRoute
              path="/processual_query/:document_type?/:id?"
              component={ProcessualQuery}
            />
            <ContentRoute
              exact
              path={`${ROUTES_PATH.LAWSUIT_MONITOR.BASE}${ROUTES_PATH.LAWSUIT_MONITOR.SUBSCRIPTION}`}
              component={SalesLandingPage}
            />
            <ContentRoute
              path={`${ROUTES_PATH.LAWSUIT_MONITOR.BASE}${ROUTES_PATH.LAWSUIT_MONITOR.LAWSUITS}/:page?/:id?`}
              component={LawsuitsMonitor}
            />
            <ContentRoute
              path={`${ROUTES_PATH.LAWSUIT_MONITOR.BASE_V2}${ROUTES_PATH.LAWSUIT_MONITOR.LAWSUITS}/:page?/:id?`}
              component={LawsuitMonitorV2}
            />
            <ContentRoute
              path={`${ROUTES_PATH.LAWSUIT_MONITOR.BASE}${ROUTES_PATH.LAWSUIT_MONITOR.DEADLINES}${ROUTES_PATH.LAWSUIT_MONITOR.COMPLETED_DEADLINES}`}
              exact
            >
              <Deadlines tab="concluidos" />
            </ContentRoute>
            <ContentRoute
              path={`${ROUTES_PATH.LAWSUIT_MONITOR.BASE}${ROUTES_PATH.LAWSUIT_MONITOR.DEADLINES}/:tab?/:id?`}
              component={Deadlines}
            />
            <ContentRoute path="/oportunidades/:type" component={Oportunidades} />
            <ContentRoute path="/oportunidades" component={Oportunidades} />
            {/* A listagem de diligências vive na aba de Oportunidades; só o wizard
            tem rota própria, por ser tela cheia. */}
            <ContentRoute
              path={ROUTES_PATH.CLIENTES.CONTRATAR_CORRESPONDENTE}
              component={ContratarCorrespondente}
            />
            <ContentRoute path="/calculos" component={Calculos} />
            <ContentRoute
              path="/peticoes/selecionadas/visualizar"
              component={PeticaoSelecionadaVisualizar}
            />
            <ContentRoute
              path="/peticoes/selecionadas/:selection_name"
              component={PeticoesSelecionadas}
            />
            <ContentRoute
              path="/peticoes/listagem/:category_id/:filter"
              component={PeticoesListagem}
            />
            <ContentRoute
              path="/peticoes/listagem/:category_id"
              component={PeticoesListagem}
            />
            <ContentRoute path="/peticoes/listagem/" component={PeticoesListagem} />
            <ContentRoute
              path="/peticoes/visualizar/:petition_id/:category_id?"
              component={PeticaoVisualizar}
            />
            <ContentRoute path="/peticoes" component={PeticoesListagem} />
            <ContentRoute
              path="/trabalhista/:calculo_id/:status?"
              component={Trabalhista}
            />
            <ContentRoute path="/trabalhista" component={Trabalhista} />
            <ContentRoute
              path="/atualizacao/:calculo_id/:status?"
              component={Atualizacao}
            />
            <ContentRoute path="/atualizacao" component={Atualizacao} />
            <ContentRoute
              path="/atualizacao_completo/:calculo_id/:status?"
              component={AtualizacaoCompleto}
            />
            <ContentRoute
              path="/atualizacao_completo"
              component={AtualizacaoCompleto}
            />
            <ContentRoute path="/consulta" component={Consulta} />
            <ContentRoute
              path="/jurisprudencia/:id"
              component={JurisprudenciaCompleta}
            />
            <ContentRoute path="/jurisprudencia" component={Jurisprudencia} />{' '}
            {/* Estudos (Academy): hub de estudo OAB do aluno no embed.
                /academy/* segue existindo como deep-link das telas internas
                (trilha, game, simulado, banco, ranking, versus). */}
            <Route
              path={ROUTES_PATH.MAIN.ESTUDOS}
              render={academyOnly(() => <JusrecrutaEmbed path="/estudos" />)}
            />
            <Route
              path={ROUTES_PATH.MAIN.ACADEMY}
              render={academyOnly(({ location }) => (
                <JusrecrutaEmbed
                  path={
                    location.pathname === ROUTES_PATH.MAIN.ACADEMY
                      ? '/estudos'
                      : // A query string vai junto: /academy/ligas?aba=disponiveis abre
                        // a aba certa; /academy/game?area=... escolhe a disciplina.
                        `${location.pathname}${location.search}`
                  }
                />
              ))}
            />{' '}
            {/* Carreira (JusRecruta): as vagas de estágio do aluno, no embed
                (a /area do jusrecruta-front, que esconde o header próprio quando
                está em iframe). A home do aluno aponta para cá no "Ver as vagas". */}
            <Route
              path={ROUTES_PATH.MAIN.CARREIRA}
              render={academyOnly(() => <JusrecrutaEmbed path="/area" />)}
            />
            {/* Detalhe do cliente. Sem regex no path (em JSX o escape do \\ é
                traiçoeiro e já quebrou a rota); :id puro, com guarda numérica no
                componente. Fica ANTES de /clientes.
                :aba? captura a sub-rota da ficha no MFE (ex.: /documentos) — sem
                ela o match parcial engolia o segmento e o deep link abria a aba
                errada, sem erro. `exact` para o segmento extra não sumir. */}
            {/* Rotas de segmento FIXO precisam vir ANTES de /clientes/:id:
                `arquivados` e `oab-sugestoes` casariam como :id, e a guarda
                numérica do ClientesDetalhe mandaria os dois para /clientes com
                um Redirect irreversível. */}
              <ContentRoute
                exact
                path="/clientes/arquivados"
                component={ClientesArquivados}
              />
              <ContentRoute
                exact
                path="/clientes/oab-sugestoes"
                component={ClientesOabSugestoes}
              />
              <ContentRoute
                exact
                path="/clientes/:id/:aba?"
                component={ClientesDetalhe}
              />
              <ContentRoute path="/clientes" component={Clientes} />
              <ContentRoute
                path={ROUTES_PATH.CLIENTES.CRIADOR_CONTEUDO}
                component={ProtectedCriadorConteudo}
              />
              {/*
                A ficha e as pastas vêm ANTES da carteira, e a ordem é load-bearing: sem
                `exact` nelas, a rota da carteira casaria `/juscore-rh/detalhes/7`
                e `/juscore-rh/pastas`, e só ela renderizaria. É a armadilha anotada acima
                nas rotas de `/clientes`.
              */}
              <ContentRoute
                exact
                path={ROUTES_PATH.JUSCORE_RH.DETALHES}
                component={ProtectedJusScoreRhDetalhes}
              />
              <ContentRoute
                exact
                path={ROUTES_PATH.JUSCORE_RH.PASTA}
                component={ProtectedJusScoreRhPasta}
              />
              <ContentRoute
                exact
                path={ROUTES_PATH.JUSCORE_RH.PASTAS}
                component={ProtectedJusScoreRhPastas}
              />
              <ContentRoute
                path={ROUTES_PATH.JUSCORE_RH.BASE}
                component={ProtectedJusScoreRh}
              />
              <ContentRoute
                path={ROUTES_PATH.CONFIGURACOES.PLAN_AND_PAYMENT}
                component={PlanAndPayment}
              />
              <ContentRoute
                path={ROUTES_PATH.CONFIGURACOES.ACCOUNT_PROFILE}
                component={PerfilRoute}
              />
              <ContentRoute
                path={ROUTES_PATH.CONFIGURACOES.SECURITY}
                component={Security}
              />
              <ContentRoute
                path={ROUTES_PATH.CONFIGURACOES.OFFICE}
                component={Escritorio}
              />
              <ContentRoute
                path="/revisional/:calculo_id/:status?"
                component={Revisional}
              />
              <ContentRoute path="/revisional" component={Revisional} />
              <ContentRoute path="/agenda" component={Agenda} />
              <ContentRoute
                path="/usuarios/form/:cliente_usuario_id"
                component={ProtectedUsuariosForm}
              />
              <ContentRoute path="/calculadoras" component={Calculators} />
              <ContentRoute
                path="/pasep/:calculationId/:status?"
                component={PASEP}
              />
              <ContentRoute path="/pasep" component={PASEP} />
              <ContentRoute
                path="/inss/:calculo_id/:status?"
                component={InssNews}
              />
              <ContentRoute path="/inss" component={InssNews} />
              <ContentRoute path="/rmc/:calculo_id/:status?" component={Rmc} />
              <ContentRoute path="/rmc" component={Rmc} />
              <ContentRoute
                path="/superendividamento/:calculo_id/:status?"
                component={OverDebtsCalculator}
              />
              <ContentRoute
                path="/superendividamento"
                component={OverDebtsCalculator}
              />
              <ContentRoute
                path="/divorcio/:calculo_id/:status?"
                component={Divorcio}
              />
              <ContentRoute
                path="/acima-teto/:calculation_id/:status?"
                component={AcimaTetoCalculator}
              />
              <ContentRoute
                path="/acima-teto"
                component={AcimaTetoCalculator}
              />
              <ContentRoute
                path="/planejamento-previdenciario/:calculation_id?/:status?"
                component={PensionPlanningCalculator}
              />
              <ContentRoute
                path="/previdenciaria/:calculation_id/:status?"
                component={PensionCalculator}
              />
              <ContentRoute
                path="/previdenciaria"
                component={PensionCalculator}
              />
              <ContentRoute path="/divorcio" component={Divorcio} />
              <ContentRoute path="/sobre" component={About} />
              <ContentRoute
                path="/usuarios/form"
                component={ProtectedUsuariosForm}
              />
              <ContentRoute path="/usuarios" component={ProtectedUsuarios} />
              <ContentRoute
                path="/assinaturas/form"
                component={AssinaturasForm}
              />
              <ContentRoute path="/assinaturas" component={Assinaturas} />
              <ContentRoute
                path="/influencers/cupom_influencer"
                component={PageListInfluencer}
              />
              <ContentRoute
                path="/resultado/:feature_type_name/:id/:query?"
                component={MAIN}
              />
              <ContentRoute
                path="/progressao-regime/:calculo_id/:status?"
                component={Penal}
              />
              <ContentRoute path="/progressao-regime" component={Penal} />
              <ContentRoute
                path="/dosimetria-pena/:calculo_id/:status?"
                component={Dosimetry}
              />
              <ContentRoute path="/dosimetria-pena" component={Dosimetry} />
              <Route path="/terms" component={TermsOfUse} />
              <Route
                path="/gpt"
                render={() => (
                  <JusGPTBusinessProvider>
                    <Switch>
                      <Route path="/gpt/config" component={Config} />
                      <Route path="/gpt/business" component={BusinessLpGuard} />
                      <Route path="/gpt/reports" component={ReportsPage} />
                      <Route path="/gpt" component={JusGPT} />
                    </Switch>
                  </JusGPTBusinessProvider>
                )}
              />
              <Route path="/page/blog/editor" component={BlogEditorPage} />
              <Route path="/page" component={JusPage} />
              <Route path="/space/domain" component={DomainFormPage} />
              <Route
                path="/space/whatsapp/gerenciamento-equipe"
                component={TeamManagement}
              />
              <Route
                path="/space/whatsapp/configuracoes-robo"
                component={RobotSettings}
              />
              <Route
                path={ROUTES_PATH.JUSZAP.MEETINGS}
                component={MeetingScheduler}
              />
              <Route
                path={ROUTES_PATH.JUSZAP.CUSTOM_FLOW}
                component={CustomFlowConfiguration}
              />
              <Route path="/space/whatsapp" component={WhatsAppBusiness} />
              <Route path="/space" component={JusSpace} />
              <Route path="/mail" component={JusMailPage} />
              <Route path="/jusmail-redirect" component={JusMailRedirect} />
              <Route path="/assistente-whatsapp" component={JusZapLpPage} />
              <Route
                path={ROUTES_PATH.GAMIFICATION.HISTORICO}
                component={PointsHistory}
              />
              <Route
                path={ROUTES_PATH.GAMIFICATION.BASE}
                component={Gamification}
              />
              <Route
                path={ROUTES_PATH.AFILIADOS.BASE}
                component={AfiliadosRoute}
              />
              {/* As rotas internas vêm antes da LP: /contabilidade casa com
                todas elas por prefixo. */}
            <Route path={ROUTES_PATH.CONTABILIDADE.DASHBOARD}>
              <ContabilidadePainelGuard>
                <JusContPainelNotasFiscais />
              </ContabilidadePainelGuard>
            </Route>
            <Route path={ROUTES_PATH.CONTABILIDADE.NOTAS}>
              <ContabilidadePainelGuard>
                <JusContNotasEmitidas />
              </ContabilidadePainelGuard>
            </Route>
            <Route
              path={ROUTES_PATH.CONTABILIDADE.CONTATO}
              component={JusContContatoEspecialista}
            />
            <Route
              path={ROUTES_PATH.CONTABILIDADE.LANDING_PAGE}
              component={ContabilidadeLpGuard}
            />
            <Route
              path="/addons/capicandia/checkout"
              component={CapicandiaCheckout}
            />
            <Route path={ROUTES_PATH.JUSCLASS.BASE} component={JusClass} />
            <Redirect to="/error/error-v1" />
          </Switch>
          </OnboardingToolsGate>
        </OnboardingOfficeGate>
      </OnboardingOabGate>
      </OnboardingAddressGate>
      </OnboardingWelcomeGate>
    </Suspense>
    </OabCaptureTriggerProvider>
  );
}
